import { useRef, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import Hls from "hls.js/dist/hls";

export default function ShortyVideo({
  hlsUrl,
  innerVideoRef, // Ref на внутренний video элемент для кеширования (передается извне)
  mainVideoElement = null, // Главный video элемент из ShortyVirtualFeed
  onReady = null, // Callback когда HLS готов к переключению
  onError = null, // Callback для ошибок
  // drm = null, // DRM настройки (пока не используем) - убрано так как не используется
  start = null, // Начальная позиция
  children, // Render prop функция
}) {
  const hlsRef = useRef(null); // HLS.js instance
  const [hlsReady, setHlsReady] = useState(false);
  const [error, setError] = useState(null); // eslint-disable-line no-unused-vars
  const [isLoading, setIsLoading] = useState(false); // eslint-disable-line no-unused-vars

  // Ref для отслеживания подключения к главному видео
  const isAttachedToMainRef = useRef(false);

  // Теперь onReady и onError стабильные - не нужны рефы

  // innerVideoRef теперь передается из ShortyVideoContent где рендерится StyledVideo
  // Никаких createElement не нужно - video элемент создается через JSX

  // Переключение HLS с кеша на главный video элемент
  const attachToMainVideo = useCallback(() => {
    if (
      !mainVideoElement ||
      !hlsReady ||
      !innerVideoRef.current ||
      isAttachedToMainRef.current
    ) {
      return;
    }

    try {
      console.log("ShortyVideo: Switching HLS to main video element");
      isAttachedToMainRef.current = true;

      const wasPlaying = !innerVideoRef.current.paused;

      if (hlsRef.current) {
        // HLS.js режим
        hlsRef.current.detachMedia();
        hlsRef.current.attachMedia(mainVideoElement);
      } else {
        // Нативный HLS режим
        console.log(
          "ShortyVideo: Native HLS switching - inner video details:",
          {
            innerVideoSrc: innerVideoRef.current.src,
            innerVideoSrcObject: innerVideoRef.current.srcObject,
            innerVideoCurrentTime: innerVideoRef.current.currentTime,
            innerVideoPaused: innerVideoRef.current.paused,
          }
        );

        // Копируем src вместо srcObject для нативного HLS
        if (innerVideoRef.current.src) {
          mainVideoElement.src = innerVideoRef.current.src;
          console.log("ShortyVideo: Copied src to main video");
        } else {
          console.log("ShortyVideo: No src to copy from inner video");
        }
      }

      mainVideoElement.currentTime = 0;

      // Восстанавливаем воспроизведение только если главное видео не на паузе
      // (пользователь мог поставить на паузу через контролы)
      if (wasPlaying && !mainVideoElement.paused) {
        console.log("ShortyVideo: Restoring playback on main video");
        mainVideoElement.play().catch(console.error);
      } else if (wasPlaying && mainVideoElement.paused) {
        console.log(
          "ShortyVideo: Skipping autoplay - main video is paused by user"
        );
      }
    } catch (err) {
      console.error("ShortyVideo: Error switching to main video", err);
      isAttachedToMainRef.current = false;
      setError(err);
      if (onError) onError(err);
    }
  }, [mainVideoElement, hlsReady, start, onError]);

  // Переключение HLS обратно на кеш
  const detachFromMainVideo = useCallback(() => {
    if (
      !mainVideoElement ||
      !innerVideoRef.current ||
      !isAttachedToMainRef.current
    ) {
      console.log("ShortyVideo: Skipping detach -", {
        mainVideoElement: !!mainVideoElement,
        innerVideoRef: !!innerVideoRef.current,
        isAttachedToMainRef: isAttachedToMainRef.current,
      });
      return;
    }

    try {
      console.log("ShortyVideo: Switching HLS back to cache");

      const currentTime = mainVideoElement.currentTime;
      const wasPlaying = !mainVideoElement.paused;

      // Останавливаем воспроизведение
      if (wasPlaying) {
        mainVideoElement.pause();
      }

      if (hlsRef.current) {
        // HLS.js режим
        hlsRef.current.detachMedia();
        hlsRef.current.attachMedia(innerVideoRef.current);
      } else {
        // Нативный HLS режим
        mainVideoElement.src = "";
      }

      // Восстанавливаем позицию в кеше
      if (currentTime > 0) {
        innerVideoRef.current.currentTime = currentTime;
      }

      isAttachedToMainRef.current = false;
    } catch (err) {
      console.error("ShortyVideo: Error switching back to cache", err);
      isAttachedToMainRef.current = false;
    }
  }, [mainVideoElement]);

  // Создание HLS при изменении URL
  useEffect(() => {
    console.log("ShortyVideo: useEffect triggered - HLS initialization", {
      hlsUrl: !!hlsUrl,
      innerVideoRef: !!innerVideoRef.current,
      hlsReady,
      hasExistingHls: !!hlsRef.current,
      mainVideoElement: !!mainVideoElement,
      isAttachedToMainRef: isAttachedToMainRef.current,
    });

    if (!hlsUrl || !innerVideoRef.current) return;

    // Предотвращаем пересоздание если HLS уже готов
    // Для HLS.js проверяем hlsRef.current, для нативного HLS проверяем что innerVideo уже имеет src
    if (hlsReady && (hlsRef.current || innerVideoRef.current.src)) {
      console.log("ShortyVideo: Skipping HLS recreation - already ready", {
        hasHlsInstance: !!hlsRef.current,
        hasNativeHlsSrc: !!innerVideoRef.current.src,
      });
      return;
    }

    let mounted = true;
    setIsLoading(true);
    setError(null);
    setHlsReady(false);

    // Определяем целевой video элемент: mainVideo если есть, иначе innerVideo
    const targetVideoElement = mainVideoElement || innerVideoRef.current;
    const isUsingMainVideo = !!mainVideoElement;

    console.log(
      "ShortyVideo: Initializing HLS on",
      isUsingMainVideo ? "main video" : "inner video"
    );

    const initHls = () => {
      if (typeof window !== "undefined" && Hls && Hls.isSupported()) {
        // Уничтожаем предыдущий instance
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }

        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          startPosition: start || -1,
          capLevelToPlayerSize: true,
        });

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!mounted) return;
          console.log(
            "ShortyVideo: HLS manifest parsed on",
            isUsingMainVideo ? "main video" : "inner video"
          );
          setHlsReady(true);
          setIsLoading(false);

          // Отмечаем что подключены к главному видео, если использовали его
          if (isUsingMainVideo) {
            console.log(
              "ShortyVideo: HLS.js initialized directly on main video - setting isAttachedToMainRef = true"
            );
            isAttachedToMainRef.current = true;
          } else {
            console.log(
              "ShortyVideo: HLS.js initialized on inner video - isAttachedToMainRef remains false"
            );
          }

          if (onReady) onReady({ hlsInstance: hls, hlsUrl });
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (!mounted) return;
          console.error("ShortyVideo: HLS error", data);

          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                setError(data);
                if (onError) onError(data);
                break;
            }
          }
        });

        hls.loadSource(hlsUrl);
        hls.attachMedia(targetVideoElement);
        hlsRef.current = hls;
      } else if (
        targetVideoElement.canPlayType("application/vnd.apple.mpegurl")
      ) {
        // Нативный HLS (Safari)
        console.log(
          "ShortyVideo: Using native HLS support on",
          isUsingMainVideo ? "main video" : "inner video"
        );
        targetVideoElement.src = hlsUrl;

        // Настройки для главного видео
        if (isUsingMainVideo) {
          console.log(
            "ShortyVideo: Native HLS initialized directly on main video - setting isAttachedToMainRef = true"
          );
          isAttachedToMainRef.current = true;
        } else {
          console.log(
            "ShortyVideo: Native HLS initialized on inner video - isAttachedToMainRef remains false"
          );
          targetVideoElement.muted = true;
          targetVideoElement.play();
        }

        const handleCanPlay = () => {
          if (!mounted) return;
          console.log(
            "ShortyVideo: Native HLS ready on",
            isUsingMainVideo ? "main video" : "inner video"
          );
          if (onReady) onReady({ hlsInstance: null, hlsUrl });
          targetVideoElement.removeEventListener("canplay", handleCanPlay);
        };

        targetVideoElement._nativeHlsHandler = handleCanPlay;
        targetVideoElement.addEventListener("canplay", handleCanPlay);

        if (start) {
          targetVideoElement.currentTime = start;
        }
        setIsLoading(false);
        setHlsReady(true);
      } else {
        const errorMsg = "HLS is not supported in this browser";
        setError({ message: errorMsg });
        if (onError) onError({ message: errorMsg });
      }
    };

    initHls();

    return () => {
      mounted = false;

      // Очищаем native HLS handler с правильного элемента
      if (targetVideoElement._nativeHlsHandler) {
        targetVideoElement.removeEventListener(
          "canplay",
          targetVideoElement._nativeHlsHandler
        );
        targetVideoElement._nativeHlsHandler = null;
      }

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      // Сбрасываем состояние подключения
      isAttachedToMainRef.current = false;
    };
  }, [hlsUrl, start, onReady, onError, mainVideoElement]); // Теперь все функции стабильные

  // Переключение HLS с innerVideo на mainVideo, если mainVideo появился позже
  useEffect(() => {
    console.log("ShortyVideo: useEffect triggered - HLS switching check", {
      mainVideoElement: !!mainVideoElement,
      hlsReady,
      isAttachedToMainRef: isAttachedToMainRef.current,
      shouldSwitch:
        mainVideoElement && hlsReady && !isAttachedToMainRef.current,
    });

    if (mainVideoElement && hlsReady && !isAttachedToMainRef.current) {
      console.log("ShortyVideo: Conditions met - calling attachToMainVideo()");
      attachToMainVideo();
    }
  }, [mainVideoElement, hlsReady, attachToMainVideo]);

  // Cleanup при размонтировании компонента
  useEffect(() => {
    return () => {
      if (isAttachedToMainRef.current) {
        detachFromMainVideo();
      }
    };
  }, [detachFromMainVideo]);

  // Вызываем render prop функцию
  if (typeof children === "function") {
    // Передаем ref для preload/thumbnail (не .current!)
    // Все контролы и состояние управляются на уровне ShortyVirtualFeed
    return children(innerVideoRef);
  }

  return null;
}

ShortyVideo.propTypes = {
  hlsUrl: PropTypes.string,
  innerVideoRef: PropTypes.object.isRequired,
  mainVideoElement: PropTypes.object,
  onReady: PropTypes.func,
  onError: PropTypes.func,
  start: PropTypes.number,
  children: PropTypes.func.isRequired,
};
